import { captureException } from '@sentry/nextjs';
import { formatInTimeZone } from 'date-fns-tz';
import { enUS } from 'date-fns/locale';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';

import client from '@/apollo-client';
import Layout from '@/components/Layout';
import VenueDetail, { VenueDetailProps } from '@/components/Pages/VenueDetail';
import {
  VenueDocument,
  VenueQuery,
  VenueQueryVariables,
} from '@/generated/graphql';

export default function VenuePage(props: {
  venue: NonNullable<VenueQuery['venue']>;
}) {
  const { venue } = props;

  const venueDetailProps: VenueDetailProps = {
    imageUrl: venue.imgUrl,
    name: venue.name,
    address: venue.address,
    websiteUrl: venue.websiteUrl || undefined,
    facebookUrl: venue.facebookUrl || undefined,
    instagramUrl: venue.instagramUrl || undefined,
    events: venue.events.edges.map((event) => ({
      venueName: venue.name,
      title: event.node.title,
      itinerary: formatInTimeZone(
        event.node.startDateTime,
        venue.timezone,
        `EEE MMM dd, hh:mmaa zzz`, // Sun Apr 10, 10:00PM EST
        { locale: enUS },
      ),
      imageUrl: event.node.imgUrl,
      id: event.node.id,
      cancelled: event.node.isCancelled,
    })),
  };

  return (
    <Layout headerType={`float`}>
      <NextSeo
        title={`${venue.name} | Someday`}
        description={`Find tickets to curated events at ${venue.name}. Cut through the noise with easy check-out and check-in.`}
        openGraph={{
          title: `${venue.name} | Someday`,
          description: `Find tickets to curated events at ${venue.name}. Cut through the noise with easy check-out and check-in.`,
          images: [
            {
              url: venue.imgUrl,
            },
          ],
        }}
      />
      <VenueDetail {...venueDetailProps} />
    </Layout>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    const { query } = context;
    const { id } = query;

    if (typeof id !== `string`) {
      return {
        notFound: true,
      };
    }

    const { data } = await client.query<VenueQuery, VenueQueryVariables>({
      query: VenueDocument,
      variables: { id: id, datetimeGt: new Date().toISOString() },
    });

    if (!data.venue) {
      return {
        notFound: true,
      };
    }

    return {
      props: { venue: data.venue },
    };
  } catch (error) {
    captureException(error);

    return {
      redirect: {
        destination: `/500`,
        permanent: false,
      },
    };
  }
};
